// src/pages/admin/UserLoginDetails.jsx
import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../components/HeaderBar";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Printer, FileSpreadsheet, Search, ChevronLeft, ChevronRight } from "lucide-react";

import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";

const BASE_URL = "https://nissi-india.com";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

function debounce(fn, wait = 350) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

// convert yyyy-MM-dd (from <input type="date">) -> dd/MM/yyyy (server expects)
function isoToDMY(iso) {
  if (!iso) return "";
  const [yyyy, mm, dd] = iso.split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}/${mm}/${yyyy}`;
}

function safeStr(v) {
  return v === null || v === undefined ? "" : String(v);
}

function escapeHtml(str) {
  const s = safeStr(str);
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

/**
 * Prints ONLY the currently visible rows (current page + applied filters)
 * Creates a new window with a printable HTML document and calls window.print()
 */
function buildPrintHtml({
  title = "Print",
  companyName = "",
  reportTitle = "",
  metaLeft = "",
  metaRight = "",
  headerBg = "#0f766e",
  rows = [],
}) {
  const safeStr = (v) => (v === null || v === undefined ? "" : String(v));
  const escapeHtml = (str) =>
    safeStr(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const tableRows =
    rows.length > 0
      ? rows
          .map(
            (r) => `
      <tr>
        <td style="text-align:center;">${escapeHtml(r.slno)}</td>
        <td>${escapeHtml(r.fld_date)}</td>
        <td>${escapeHtml(r.fld_intime)}</td>
        <td>${escapeHtml(r.fld_outtime)}</td>
        <td>${escapeHtml(r.fld_username)}</td>
        <td>${escapeHtml(r.fld_otp)}</td>
        <td>${escapeHtml(r.fld_mobile)}</td>
        <td>${escapeHtml(r.fld_mail)}</td>
        <td>${escapeHtml(r.fld_ipaddress)}</td>
      </tr>
    `
          )
          .join("")
      : `<tr><td colspan="9" style="text-align:center;padding:16px;">No Records Found</td></tr>`;

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: A4; margin: 10mm; }
    body { font-family: Arial, Helvetica, sans-serif; color: #111; }
    .header { text-align: center; margin-bottom: 10px; }
    .company { font-size: 20px; font-weight: 700; margin: 0; }
    .report { font-size: 16px; font-weight: 700; margin: 6px 0 0; text-decoration: underline; }
    .meta {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      font-size: 12px;
      margin: 10px 0 12px;
    }
    table { width: 100%; border-collapse: collapse; }
    thead th {
      border: 1px solid #333;
      padding: 6px 8px;
      font-size: 12px;
      background: ${headerBg};
      color: #fff;
      text-align: left;
    }
    tbody td {
      border: 1px solid #333;
      padding: 6px 8px;
      font-size: 12px;
      vertical-align: top;
    }
    tbody tr:nth-child(even) td { background: #f7fafc; }
    thead { display: table-header-group; }
    .small { font-size: 11px; color: #444; margin-top: 10px; }
  </style>
</head>
<body>
  <div>
    <div class="header">
      ${companyName ? `<p class="company">${escapeHtml(companyName)}</p>` : ""}
      ${reportTitle ? `<p class="report">${escapeHtml(reportTitle)}</p>` : ""}
    </div>

    <div class="meta">
      <div>${metaLeft}</div>
      <div>${metaRight}</div>
    </div>

    <table>
      <thead>
        <tr>
          <th style="width:55px;">S.No</th>
          <th style="width:105px;">Date</th>
          <th style="width:90px;">InTime</th>
          <th style="width:90px;">OutTime</th>
          <th style="width:120px;">UserName</th>
          <th style="width:80px;">OTP</th>
          <th style="width:120px;">Mobile</th>
          <th>Email</th>
          <th style="width:120px;">IP</th>
        </tr>
      </thead>
      <tbody>
        ${tableRows}
      </tbody>
    </table>

    <div class="small">
      Printed on: ${escapeHtml(new Date().toLocaleString())}
    </div>
  </div>
</body>
</html>`;
}

function printHtmlInHiddenIframe(html) {
  const old = document.getElementById("print-iframe");
  if (old) old.remove();

  const iframe = document.createElement("iframe");
  iframe.id = "print-iframe";
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.style.opacity = "0";
  iframe.style.pointerEvents = "none";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) return;

  doc.open();
  doc.write(html);
  doc.close();

  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally {
      setTimeout(() => {
        iframe.remove();
      }, 1500);
    }
  };

  setTimeout(() => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {}
  }, 350);
}

export default function UserLoginDetails() {
  const navigate = useNavigate();

  const { skin, isDark } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const headerBgClass = `bg-${baseColor}-${baseShade}`;
  const headerTextClass = "text-white";
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryBtnHover = `hover:bg-${baseColor}-700`;

  const controlClass =
    "w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300";

  const lightBtnClass =
    "inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 hover:bg-slate-50 disabled:opacity-50";

  const pageBtnClass =
    "inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1 text-gray-900 disabled:opacity-50";

  const [fromDateISO, setFromDateISO] = useState("");
  const [toDateISO, setToDateISO] = useState("");

  const fromDate = useMemo(() => isoToDMY(fromDateISO), [fromDateISO]);
  const toDate = useMemo(() => isoToDMY(toDateISO), [toDateISO]);

  const [username, setUsername] = useState("");

  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const latestReq = useRef(0);

  const fetchList = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextPageSize = opts.pageSize ?? pageSize;

    setLoading(true);
    const reqId = Date.now();
    latestReq.current = reqId;

    try {
      const r = await axios.get(`${BASE_URL}/api/user-login-details/list`, {
        headers: tenantHeaders(),
        params: tenantParams({
          fromDate: fromDate || "",
          toDate: toDate || "",
          username: username || "",
          page: nextPage,
          pageSize: nextPageSize,
        }),
      });

      if (latestReq.current !== reqId) return;

      setRows(r.data?.rows || []);
      setTotal(Number(r.data?.total || 0));
      setPage(Number(r.data?.page || nextPage));
      setPageSize(Number(r.data?.pageSize || nextPageSize));
    } catch (e) {
      console.error(e);
      toast.error("Failed to load user login details");
    } finally {
      if (latestReq.current === reqId) setLoading(false);
    }
  };

  useEffect(() => {
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSuggestions = async (q) => {
    const s = String(q || "").trim();
    if (!s) {
      setSuggestions([]);
      return;
    }
    try {
      const r = await axios.get(`${BASE_URL}/api/user-login-details/meta/usernames`, {
        headers: tenantHeaders(),
        params: tenantParams({ q: s }),
      });
      setSuggestions(r.data?.users || []);
    } catch (e) {
      console.error(e);
      setSuggestions([]);
    }
  };
  const debouncedSug = useMemo(() => debounce(fetchSuggestions, 300), []);

  const onUsernameChange = (v) => {
    setUsername(v);
    setShowSug(true);
    debouncedSug(v);
  };

  const onSearch = () => {
    setPage(1);
    fetchList({ page: 1 });
  };

  const gotoPage = (p) => {
    const np = clamp(p, 1, totalPages);
    setPage(np);
    fetchList({ page: np });
  };

  const pageButtons = useMemo(() => {
    const windowSize = 5;
    const start = Math.max(1, page - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    const s2 = Math.max(1, end - windowSize + 1);
    const arr = [];
    for (let i = s2; i <= end; i++) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const onPrint = () => {
    if (loading) {
      toast.info("Please wait, records are loading...");
      return;
    }

    const headerHex = "#0f766e";

    const metaLeft = `
    <div><b>From:</b> ${fromDate || "-"}</div>
    <div><b>To:</b> ${toDate || "-"}</div>
    <div><b>User:</b> ${username || "-"}</div>
  `;

    const metaRight = `
    <div><b>Page:</b> ${page}</div>
    <div><b>Page Size:</b> ${pageSize}</div>
    <div><b>Showing:</b> ${rows.length} row(s)</div>
  `;

    const printRows = rows.map((r, idx) => ({
      slno: (page - 1) * pageSize + (idx + 1),
      fld_date: r.fld_date,
      fld_intime: r.fld_intime,
      fld_outtime: r.fld_outtime,
      fld_username: r.fld_username,
      fld_otp: r.fld_otp,
      fld_mobile: r.fld_mobile,
      fld_mail: r.fld_mail,
      fld_ipaddress: r.fld_ipaddress,
    }));

    const html = buildPrintHtml({
      title: "User Log Report",
      companyName: "",
      reportTitle: "User Log Report",
      metaLeft,
      metaRight,
      headerBg: headerHex,
      rows: printRows,
    });

    printHtmlInHiddenIframe(html);
  };

  const onExcel = () => {
    const qs = new URLSearchParams();
    if (fromDate) qs.set("fromDate", fromDate);
    if (toDate) qs.set("toDate", toDate);
    if (username) qs.set("username", username);
    const finYearId = getFinYearId();
    if (finYearId) qs.set("finYearId", finYearId);

    window.location.href = `${BASE_URL}/api/user-login-details/excel?${qs.toString()}`;
  };

  return (
    <AdminLayoutWrapper>
      <HeaderBar />
      <div className={`p-4 ${isDark ? "text-white" : "text-gray-900"}`}>
        <ToastContainer />

        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h1 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
            User Login Details
          </h1>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onPrint}
              className={lightBtnClass}
              title="Print current page"
              disabled={loading}
            >
              <Printer className="h-4 w-4" />
              Print
            </button>

            <button
              type="button"
              onClick={onExcel}
              className={lightBtnClass}
              title="Export Excel"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Excel
            </button>

            <select
              className="rounded border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900"
              value={pageSize}
              onChange={(e) => {
                const ps = Number(e.target.value);
                setPageSize(ps);
                setPage(1);
                fetchList({ page: 1, pageSize: ps });
              }}
              disabled={loading}
              title="Rows per page"
            >
              {[10, 25, 50, 100, 200].map((n) => (
                <option key={n} value={n} className="bg-white text-gray-900">
                  {n}/page
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded border bg-white p-4 text-gray-900">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800">From Date</label>
              <input
                type="date"
                className={controlClass}
                value={fromDateISO}
                onChange={(e) => setFromDateISO(e.target.value)}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-800">To Date</label>
              <input
                type="date"
                className={controlClass}
                value={toDateISO}
                onChange={(e) => setToDateISO(e.target.value)}
                min={fromDateISO || undefined}
              />
            </div>

            <div className="relative">
              <label className="mb-1 block text-sm font-medium text-gray-800">User Name</label>
              <input
                className={controlClass}
                value={username}
                onChange={(e) => onUsernameChange(e.target.value)}
                onFocus={() => {
                  setShowSug(true);
                  if (username) debouncedSug(username);
                }}
                onBlur={() => setTimeout(() => setShowSug(false), 200)}
                placeholder="Type username…"
                autoComplete="off"
              />

              {showSug && suggestions.length > 0 && (
                <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded border bg-white text-gray-900 shadow">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="block w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-slate-100"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setUsername(s);
                        setShowSug(false);
                        setSuggestions([]);
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={onSearch}
              disabled={loading}
              className={`inline-flex items-center gap-2 rounded px-4 py-2 text-white disabled:opacity-50 ${primaryBtnClass} ${primaryBtnHover}`}
            >
              <Search className="h-4 w-4" />
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 rounded border bg-white text-gray-900">
          <div className="overflow-auto">
            <table className="min-w-[1100px] w-full border-collapse">
              <thead>
                <tr className={`${headerBgClass} ${headerTextClass} text-sm`}>
                  <th className="border border-white/20 px-2 py-2 text-center">Slno</th>
                  <th className="border border-white/20 px-2 py-2">Date</th>
                  <th className="border border-white/20 px-2 py-2">InTime</th>
                  <th className="border border-white/20 px-2 py-2">OutTime</th>
                  <th className="border border-white/20 px-2 py-2">UserName</th>
                  <th className="border border-white/20 px-2 py-2">OTP</th>
                  <th className="border border-white/20 px-2 py-2">Mobile</th>
                  <th className="border border-white/20 px-2 py-2">Email</th>
                  <th className="border border-white/20 px-2 py-2 text-center">Modules</th>
                  <th className="border border-white/20 px-2 py-2">Static IP</th>
                </tr>
              </thead>

              <tbody className="text-sm text-gray-900">
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i}>
                      <td className="border px-2 py-3" colSpan={10}>
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="px-3 py-6 text-center text-slate-500" colSpan={10}>
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={r.fld_logId} className="text-gray-900 hover:bg-slate-50">
                      <td className="border px-2 py-2 text-center">
                        {(page - 1) * pageSize + (idx + 1)}
                      </td>
                      <td className="border px-2 py-2">{r.fld_date}</td>
                      <td className="border px-2 py-2">{r.fld_intime}</td>
                      <td className="border px-2 py-2">{r.fld_outtime}</td>
                      <td className="border px-2 py-2">{r.fld_username}</td>
                      <td className="border px-2 py-2">{r.fld_otp}</td>
                      <td className="border px-2 py-2">{r.fld_mobile}</td>
                      <td className="border px-2 py-2">{r.fld_mail}</td>
                      <td className="border px-2 py-2 text-center">
                        <button
                          className="rounded bg-slate-900 px-3 py-1 text-xs text-white"
                          onClick={() =>
                            navigate(`/adminMaster/UserLoginModuleDetails?logId=${r.fld_logId}`)
                          }
                        >
                          View
                        </button>
                      </td>
                      <td className="border px-2 py-2">{r.fld_ipaddress}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm text-gray-800">
            <div className="text-slate-600">
              Total: <span className="font-semibold">{total}</span>
              {total > 0 && (
                <>
                  {" "}
                  | Showing{" "}
                  <span className="font-semibold">{(page - 1) * pageSize + 1}</span> -{" "}
                  <span className="font-semibold">{Math.min(page * pageSize, total)}</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                className={pageBtnClass}
                disabled={loading || page <= 1}
                onClick={() => gotoPage(page - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>

              {pageButtons[0] > 1 && (
                <>
                  <button
                    className={pageBtnClass}
                    onClick={() => gotoPage(1)}
                    disabled={loading}
                  >
                    1
                  </button>
                  <span className="px-1 text-slate-500">…</span>
                </>
              )}

              {pageButtons.map((p) => (
                <button
                  key={p}
                  className={`rounded border border-gray-300 px-3 py-1 ${
                    p === page
                      ? "bg-slate-900 text-white"
                      : "bg-white text-gray-900"
                  }`}
                  disabled={loading}
                  onClick={() => gotoPage(p)}
                >
                  {p}
                </button>
              ))}

              {pageButtons[pageButtons.length - 1] < totalPages && (
                <>
                  <span className="px-1 text-slate-500">…</span>
                  <button
                    className={pageBtnClass}
                    onClick={() => gotoPage(totalPages)}
                    disabled={loading}
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                className={pageBtnClass}
                disabled={loading || page >= totalPages}
                onClick={() => gotoPage(page + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className={`mt-2 text-xs ${isDark ? "text-white/60" : "text-slate-500"}`}>
          Print prints ONLY current page rows ({pageSize}/page) and respects filters.
          Excel is server-side as you said.
        </div>
      </div>
    </AdminLayoutWrapper>
  );
}